import {
  Box,
  Center,
  CloseButton,
  Flex,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Kbd,
  Spinner,
  Stack,
  useBoolean,
  useColorModeValue,
  useMediaQuery,
  useOutsideClick,
  Text,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import HybridBottomSheetDrawer from "../HybridBottomSheetDrawer";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { isMacOs } from "react-device-detect";
import ReactGA from "react-ga4";
import { useHotkeys } from "react-hotkeys-hook";
import { IconType } from "react-icons";
import { BsCommand } from "react-icons/bs";
import { UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import LancasterLogo from "../../assets/lancaster-icon.svg";
import CampusButton from "../CampusButton";
import LocationButton from "../LocationButton";
import EventsSpotlight from "./EventsSpotlight";
import SearchResultRow from "./SearchResultRow";
import { useBusStopsSource } from "./sources/bus.source";
import { useEventsSource } from "./sources/event.source";
import { useLaundrettesSource } from "./sources/laundrette.source";
import { useLocationSource } from "./sources/location.source";
import { useOutletsSource } from "./sources/outlet.source";
import usePaginate from "../../hooks/usePaginate";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useAllCategories, usePoiTypes } from "../../data/mazemap.api";

const MotionBox = motion(Box);

export type SearchResult = {
  icon: IconType;
  iconColour: string;
  title: string;
  subtitle?: string;
  description?: string;
  tag?: string;
  to: string;
};

export type SearchSource<T> = {
  title: string;
  source: UseQueryResult<T[]>;
  transformer: (resolver: T) => SearchResult;
  priority: number;
};

const MapSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const searchInput = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useBoolean();
  const spotlightSearchBox = useRef<HTMLDivElement>(null);
  const background = useColorModeValue("gray.200", "gray.900");
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  useHotkeys(isMacOs ? "cmd+k" : "ctrl+k", () => searchInput.current?.focus());

  const {
    isOpen: isOpenPointsOfInterest,
    onOpen: onOpenPointsOfInterest,
    onClose: onClosePointsOfInterest,
  } = useDisclosure();

  const {
    data: categoryPoiIds,
    error: categoryPoiIdsError,
    isLoading: categoryPoiIdsIsLoading,
  } = usePoiTypes();
  const [poiTypeIds, setPoiTypeIds] = useState<number[]>([]);
  const {
    data: categories,
    error: categoriesError,
    isLoading: categoriesIsLoading,
  } = useAllCategories(poiTypeIds);

  useEffect(() => {
    if (categoryPoiIds) {
      setPoiTypeIds(categoryPoiIds);
    }
  }, [categoryPoiIds]);

  useEffect(() => {
    if (categories) {
      const fetchedData = categories.map((category: any) => ({
        id: category.id,
        title: category.title,
        icon: category.icon,
      }));
      setPoisDropdownData(fetchedData);
    }
  }, [categories]);

  const [poisDropdownData, setPoisDropdownData] = useState<
    { id: number; title: string; icon: string | null }[]
  >([]);

  const sources: SearchSource<any>[] = [
    useLocationSource(searchQuery),
    useEventsSource(searchQuery),
    useOutletsSource(searchQuery),
    useBusStopsSource(searchQuery),
    useLaundrettesSource(searchQuery),
  ];
  const isLoading = sources.some((s) => s.source.isLoading);
  const navigate = useNavigate();

  useOutsideClick({
    ref: spotlightSearchBox,
    handler: setFocus.off,
    enabled: focus,
  });

  const allSearchResults = sources
    ?.filter((s) => !s.source.isLoading)
    ?.sort((a, b) => b.priority - a.priority)
    ?.flatMap((s) => s.source.data?.map(s.transformer) ?? [])
    ?.flat();

  const {
    data: searchResults,
    next,
    prev,
    currentPage,
    totalPages,
  } = usePaginate(searchQuery, allSearchResults, 1, isMobile ? 4 : null);

  const closeSearch = useCallback(() => {
    setSearchQuery("");
    setFocus.off();
  }, [setFocus]);

  useEffect(() => {
    ReactGA.event({
      category: "search",
      action: `used map search`,
    });
  }, [searchQuery]);

  return (
    <AnimatePresence>
      {focus || searchQuery.length > 0 ? (
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          key="backdrop"
          w="100vw"
          h="100vh"
          position="fixed"
          top={0}
          left={0}
          zIndex={5}
          bg="blackAlpha.600"
          onClick={() => setSearchQuery("")}
        />
      ) : null}

      <Flex
        justifyContent="center"
        alignItems="center"
        w="full"
        position="fixed"
        pointerEvents="none"
        top={0}
        zIndex={10}
        left={0}
        p={5}
        px={8}
      >
        <Stack
          pointerEvents="all"
          p={3}
          ref={spotlightSearchBox}
          rounded="lg"
          spacing={5}
          w="full"
          maxW="container.md"
          bg={focus || searchQuery.length ? background : undefined}
          onTouchStart={(e) => {
            if (e.target !== searchInput.current) {
              searchInput.current?.blur();
            }
          }}
        >
          <Flex gap={2}>
            <InputGroup flex={1}>
              <InputLeftElement>
                <Image
                  src={LancasterLogo}
                  opacity={0.8}
                  h={5}
                  _dark={{ filter: "grayscale(90%)" }}
                />
              </InputLeftElement>
              <Input
                boxShadow="md"
                variant="filled"
                bg={background}
                ref={searchInput}
                _hover={{ bg: background, filter: "brightness(1.2)" }}
                filter={focus ? "brightness(1.2)" : undefined}
                placeholder="Search everything..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={setFocus.on}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const selected = searchResults[0];
                    if (selected) {
                      navigate(selected.to);
                      closeSearch();
                      searchInput.current?.blur();
                    }
                  } else if (e.key === "Escape") {
                    setFocus.off();
                    searchInput.current?.blur();
                  }
                }}
              />
              {searchQuery.length > 0 ? (
                <InputRightElement>
                  <CloseButton onClick={() => setSearchQuery("")} />
                </InputRightElement>
              ) : null}
              {!focus && searchQuery.length === 0 ? (
                <InputRightElement w="auto" gap={2} pointerEvents="none">
                  {!isMobile ? (
                    <Flex gap={1}>
                      <Kbd pt={1}>
                        {isMacOs ? (
                          <Icon as={BsCommand} display="inline" w={3} h={3} />
                        ) : (
                          "ctrl"
                        )}
                      </Kbd>
                      <Kbd alignItems="center" display="flex" mr={2}>
                        K
                      </Kbd>
                    </Flex>
                  ) : null}
                </InputRightElement>
              ) : null}
            </InputGroup>
            <LocationButton />
            <CampusButton />
          </Flex>
          {focus && searchQuery.length === 0 && <EventsSpotlight />}
          {searchQuery.length > 0 ? (
            <Stack
              maxH={isMobile ? undefined : "60vh"}
              overflowY={isMobile ? undefined : "scroll"}
            >
              {isMobile ? (
                <Flex align="center" justify="space-between">
                  <IconButton
                    size="sm"
                    onClick={prev}
                    icon={<Icon as={FaChevronLeft} />}
                    aria-label="Previous page"
                  />
                  <Text>
                    Page {currentPage} of {totalPages}
                  </Text>
                  <IconButton
                    size="sm"
                    onClick={next}
                    icon={<Icon as={FaChevronRight} />}
                    aria-label="Next page"
                  />
                </Flex>
              ) : null}
              {searchResults
                ? searchResults?.map((result, i) => (
                    <SearchResultRow
                      searchResult={result}
                      onClick={closeSearch}
                    />
                  ))
                : null}
              {isLoading ? (
                <Center p={3}>
                  <Spinner />
                </Center>
              ) : null}
            </Stack>
          ) : (
            <Box>
              {isOpenPointsOfInterest ? null : (
                <Button
                  backgroundColor={"#c0d8e0"}
                  onClick={onOpenPointsOfInterest}
                  size="sm"
                  color={"#000"}
                >
                  Open Points of Interest ({poisDropdownData.length})
                </Button>
              )}

              {/* Use the same bottom-sheet pattern as NearbyBusStops — Chakra Modal's
                  body overflow doesn't engage in the iLancaster iOS WebView, so swipes
                  on the list items register as taps. The bottom sheet handles touch
                  scroll reliably (react-spring-bottom-sheet). */}
              {isOpenPointsOfInterest ? (
                // Cap the sheet height so its top edge sits below the floating
                // search bar / "Open Points of Interest" button, instead of growing
                // up behind them.
                <HybridBottomSheetDrawer
                  maxDrawerHeight={window.innerHeight - 140}
                >
                  <Box p={8}>
                    <Flex mb={4} justify="space-between" align="center">
                      <Heading size="md">Points of Interest</Heading>
                      <CloseButton onClick={onClosePointsOfInterest} />
                    </Flex>
                    <Stack spacing={2}>
                      {poisDropdownData.map((poi) => (
                        <Button
                          key={poi.id}
                          width="100%"
                          justifyContent="flex-start"
                          onClick={() => {
                            navigate(`/location/category/${poi.id}`);
                            onClosePointsOfInterest();
                          }}
                        >
                          <Flex align="center">
                            {poi.icon ? (
                              <Image
                                src={poi.icon || ""}
                                alt={poi.title}
                                boxSize="16px"
                                mr={2}
                              />
                            ) : null}
                            <Text>{poi.title}</Text>
                          </Flex>
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                </HybridBottomSheetDrawer>
              ) : null}
            </Box>
          )}
        </Stack>
      </Flex>
    </AnimatePresence>
  );
};

export default MapSearch;
